import { call } from '@everlutionsk/helpers';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { Box, Stack, SxProps, Theme, Tooltip, Typography, styled } from '@mui/material';
import React, { ReactNode } from 'react';
import { PageBreadcrumb, PageBreadcrumbs } from '../coreComponents/breadcrumbs';
import { CircularAvatar } from '../coreComponents/circularAvatar';
import { IconButton } from '../coreComponents/iconButton';
import { ActionIconButton } from '../coreComponents/iconButtonAction';
import { RoundedAvatar } from '../coreComponents/roundedAvatar';

export interface TitleBlockProps {
  readonly title: ReactNode;
  readonly description?: ReactNode;
  readonly primaryAction?: ReactNode;
  readonly secondaryAction?: ReactNode;
  readonly badge?: ReactNode;
  readonly sx?: SxProps<Theme>;
  readonly breadcrumbs?: PageBreadcrumb[];
  readonly avatar?: ReactNode;
  readonly id?: string;
  readonly customId?: string | null;
  readonly showIds?: boolean;
  readonly idSuffix?: ReactNode;
  readonly avatarSx?: SxProps<Theme>;
  readonly showActionButton?: boolean;
  readonly onActionClick?: () => void;
  readonly onBack?: () => void;
}

export function TitleBlock({
  title,
  description,
  sx,
  avatarSx,
  primaryAction,
  secondaryAction,
  avatar,
  breadcrumbs,
  badge,
  customId,
  showIds,
  idSuffix,
  showActionButton,
  onActionClick,
  onBack,
  id
}: TitleBlockProps) {
  return (
    <Container sx={{ ...(description == null || description === '' ? { mb: -3 } : {}), ...sx }}>
      {((breadcrumbs != null && breadcrumbs.length > 0) || onBack != null) && (
        <Stack direction="row" alignItems="center" spacing={'10px'}>
          {onBack != null && (
            <IconButton variant="accent" size="s" onClick={onBack}>
              <ArrowBackIcon sx={{ width: 14, height: 14 }} />
            </IconButton>
          )}
          {breadcrumbs != null && breadcrumbs.length > 0 && <PageBreadcrumbs items={breadcrumbs} />}
        </Stack>
      )}
      <Wrapper>
        <Container>
          <Stack direction="row" spacing={3} alignItems="center">
            {avatar &&
              call((): ReactNode => {
                if (typeof avatar === 'string') {
                  return (
                    <RoundedAvatar
                      size="s"
                      src={avatar}
                      sx={{
                        border: theme => `1px solid ${theme.brandPalette.border.neutral}`,
                        ...avatarSx
                      }}
                    />
                  );
                }

                return (
                  <RoundedAvatar
                    size="s"
                    sx={{
                      border: theme => `1px solid ${theme.brandPalette.border.neutral}`,
                      ...avatarSx
                    }}
                  >
                    {avatar}
                  </RoundedAvatar>
                );
              })}

            <Typography
              variant="h4"
              sx={{
                display: 'inline-flex',
                flexDirection: 'row',
                color: theme => theme.brandPalette.foreground.neutral,
                '& > .MuiBox-root': {
                  alignSelf: 'center',
                  marginLeft: theme => theme.spacing(6)
                }
              }}
            >
              {title}
            </Typography>
            {showActionButton && <ActionIconButton size="m" onClick={() => onActionClick?.()} />}
            {badge}
          </Stack>
          {description && (
            <Typography
              variant="labelSmall"
              sx={{
                color: theme => theme.brandPalette.foreground.neutralFaded,
                maxWidth: '75%',
                marginTop: theme => theme.spacing(3)
              }}
            >
              {description}
            </Typography>
          )}
        </Container>
        <Wrapper
          sx={{ gap: theme => theme.spacing(4), pt: 2, width: 'fit-content', flexShrink: 0 }}
        >
          {primaryAction}
          {secondaryAction}
        </Wrapper>
      </Wrapper>
      {showIds && (
        <Stack direction="row" spacing={2} alignItems="center">
          <Tooltip title={id ?? ''} arrow placement="bottom">
            <Stack direction="row" alignItems="center" spacing={2} sx={{ cursor: 'default' }}>
              <CircularAvatar
                sx={{
                  width: 18,
                  height: 18,
                  border: theme => `1px solid ${theme.brandPalette.foreground.inverseFaded}`,
                  background: theme => theme.brandPalette.background.neutral,
                  '& .MuiTypography-root': {
                    color: theme => theme.brandPalette.foreground.inverseFaded,
                    fontSize: 8,
                    fontWeight: 500
                  }
                }}
              >
                ID
              </CircularAvatar>
              <Typography
                variant="subheading2XSmall"
                sx={{
                  textTransform: 'uppercase',
                  color: theme => theme.brandPalette.foreground.inverseFaded
                }}
              >
                {customId != null && customId !== '' ? customId : id}
              </Typography>
            </Stack>
          </Tooltip>
        </Stack>
      )}
    </Container>
  );
}

export interface SectionBlockProps
  extends Pick<TitleBlockProps, 'title' | 'description' | 'primaryAction' | 'secondaryAction'> {
  readonly sx?: SxProps<Theme>;
}

export function SectionBlock({
  title,
  description,
  primaryAction,
  secondaryAction,
  sx
}: SectionBlockProps) {
  return (
    <Stack direction="row" justifyContent="space-between" alignItems="baseline" width="100%">
      <Container sx={sx}>
        <Typography
          variant="h6"
          sx={{
            fontWeight: 400,
            color: theme => theme.brandPalette.foreground.neutral
          }}
        >
          {title}
        </Typography>
        {description && typeof description === 'string' ? (
          <Typography
            variant="labelXSmall"
            sx={{
              color: theme => theme.brandPalette.foreground.neutralFaded
            }}
          >
            {description}
          </Typography>
        ) : (
          description
        )}
      </Container>
      <Wrapper sx={{ gap: theme => theme.spacing(4), pt: 2, width: 'fit-content', flexShrink: 0 }}>
        {primaryAction}
        {secondaryAction}
      </Wrapper>
    </Stack>
  );
}

const Wrapper = styled(Stack)({
  flexDirection: 'row',
  alignItems: 'flex-start',
  justifyContent: 'space-between'
});

const Container = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(2),
  width: '100%'
}));
